import { useState } from "react";
import { useOAuthAccounts, useUnlinkOAuth, useSetPassword, getOAuthInitUrl } from "../../../lib/api/oauth.api";
import { useCurrentUser } from "../../../lib/api/users.api";
import { RecoveryKeyModal } from "../../auth/components/RecoveryKeyModal";
import { PassphraseInput } from "../../auth/components/PassphraseInput";
import { SectionLabel } from "./SettingsUIComponents";
import type { OAuthProvider } from "../../../shared/schemas/oauth.schema";

const MIN_WORDS = 4;
const MIN_WORD_LENGTH = 3;
const MIN_PASSWORD = 12;
type PasswordMode = "passphrase" | "classic";

function isPassphraseValid(words: readonly string[]): boolean {
  return words.filter((w) => w.length >= MIN_WORD_LENGTH).length >= MIN_WORDS;
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

function GitHubIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current text-ink">
      <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z"/>
    </svg>
  );
}

const PROVIDERS: { id: OAuthProvider; label: string; Icon: () => React.ReactNode }[] = [
  { id: "google", label: "Google", Icon: GoogleIcon },
  { id: "github", label: "GitHub", Icon: GitHubIcon },
];

export function ConnectedAccounts() {
  const { data: user } = useCurrentUser();
  const { data: accounts = [] } = useOAuthAccounts();
  const unlinkMutation = useUnlinkOAuth();
  const setPasswordMutation = useSetPassword();

  const [showSetPassword, setShowSetPassword] = useState(false);
  const [mode, setMode] = useState<PasswordMode>("passphrase");
  const [passphraseWords, setPassphraseWords] = useState<string[]>([]);
  const [classicPassword, setClassicPassword] = useState("");
  const [showClassic, setShowClassic] = useState(false);
  const [recoveryKey, setRecoveryKey] = useState<string | null>(null);

  const hasPassword = (user as Record<string, unknown> | undefined)?.hasPassword === true;

  const passwordValid = mode === "passphrase"
    ? isPassphraseValid(passphraseWords)
    : classicPassword.length >= MIN_PASSWORD;

  const getFinalPassword = () =>
    mode === "passphrase" ? passphraseWords.join(" ") : classicPassword;

  const resetForm = () => {
    setShowSetPassword(false);
    setPassphraseWords([]);
    setClassicPassword("");
    setShowClassic(false);
  };

  const handleSetPassword = () => {
    if (!passwordValid) return;
    setPasswordMutation.mutate(
      { password: getFinalPassword() },
      {
        onSuccess: (data) => {
          setRecoveryKey(data.recoveryKey);
          resetForm();
        },
      },
    );
  };

  return (
    <>
      <div className="flex flex-col gap-2 mt-3 pt-3 border-t border-border/50">
        <SectionLabel>Authentification</SectionLabel>

        <div className="rounded-lg border border-border overflow-hidden divide-y divide-border">
          {PROVIDERS.map((p) => {
            const linked = accounts.find((a) => a.provider === p.id);
            return (
              <div key={p.id} className="flex items-center gap-3 px-4 py-3">
                <div className="shrink-0"><p.Icon /></div>
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-medium text-ink">{p.label}</span>
                  <p className="text-[11px] text-ink-muted truncate">
                    {linked ? linked.email : `Connecter votre compte ${p.label}`}
                  </p>
                </div>
                {linked ? (
                  <button
                    type="button"
                    disabled={!hasPassword || unlinkMutation.isPending}
                    onClick={() => unlinkMutation.mutate(p.id)}
                    className="shrink-0 px-3 py-1.5 text-[11px] font-medium rounded-md text-ink-muted border border-border hover:text-red-500 hover:border-red-500/30 disabled:opacity-40 transition-colors"
                    title={!hasPassword ? "Definissez un mot de passe d'abord" : undefined}
                  >
                    Delier
                  </button>
                ) : (
                  <a
                    href={getOAuthInitUrl(p.id, "link")}
                    className="shrink-0 px-3 py-1.5 text-[11px] font-medium rounded-md text-ink border border-border hover:bg-surface-alt transition-colors"
                  >
                    Connecter
                  </a>
                )}
              </div>
            );
          })}
        </div>

        {!hasPassword && (
          <div className="flex flex-col gap-2 p-3 bg-amber-500/5 border border-amber-500/15 rounded-lg">
            <p className="text-[11px] text-amber-400 font-medium">
              Définissez un mot de passe pour sécuriser votre compte et pouvoir délier un compte OAuth.
            </p>
            {!showSetPassword ? (
              <button
                type="button"
                onClick={() => setShowSetPassword(true)}
                className="self-start px-3 py-1.5 text-[11px] font-medium rounded-lg text-amber-500 border border-amber-500/30 hover:bg-amber-500/5 transition-colors"
              >
                Définir un mot de passe
              </button>
            ) : (
              <div className="flex flex-col gap-3">
                <div className="flex rounded-lg border border-border overflow-hidden">
                  <button
                    type="button"
                    onClick={() => { setMode("passphrase"); setClassicPassword(""); }}
                    className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
                      mode === "passphrase"
                        ? "bg-accent text-white"
                        : "bg-surface-alt text-ink-muted hover:text-ink"
                    }`}
                  >
                    Phrase de passe
                  </button>
                  <button
                    type="button"
                    onClick={() => { setMode("classic"); setPassphraseWords([]); }}
                    className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
                      mode === "classic"
                        ? "bg-accent text-white"
                        : "bg-surface-alt text-ink-muted hover:text-ink"
                    }`}
                  >
                    Mot de passe
                  </button>
                </div>

                {mode === "passphrase" ? (
                  <PassphraseInput
                    words={passphraseWords}
                    onChange={setPassphraseWords}
                    minWords={MIN_WORDS}
                    autoComplete="new-password"
                  />
                ) : (
                  <div>
                    <div className="relative">
                      <input
                        type={showClassic ? "text" : "password"}
                        value={classicPassword}
                        onChange={(e) => setClassicPassword(e.target.value)}
                        placeholder="Nouveau mot de passe"
                        autoComplete="new-password"
                        className="w-full px-3 py-2 pr-16 text-sm rounded-lg border border-border bg-surface-alt
                          focus:outline-none focus:ring-2 focus:ring-accent/50 placeholder:text-ink-faint transition-shadow"
                      />
                      <button
                        type="button"
                        onClick={() => setShowClassic((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink text-xs font-medium transition-colors"
                        tabIndex={-1}
                      >
                        {showClassic ? "Masquer" : "Voir"}
                      </button>
                    </div>
                    {classicPassword.length > 0 && (
                      <p className={`mt-1.5 text-[10px] font-medium ${classicPassword.length >= MIN_PASSWORD ? "text-emerald-500" : "text-ink-muted"}`}>
                        {classicPassword.length}/{MIN_PASSWORD} caractères minimum
                      </p>
                    )}
                  </div>
                )}

                <div className="flex gap-2 justify-end">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-3 py-1.5 text-[11px] text-ink-muted hover:text-ink rounded-lg"
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    disabled={!passwordValid || setPasswordMutation.isPending}
                    onClick={handleSetPassword}
                    className="px-3 py-1.5 text-[11px] font-medium text-white bg-accent rounded-lg hover:opacity-80 disabled:opacity-40 transition-opacity"
                  >
                    {setPasswordMutation.isPending ? "..." : "Définir"}
                  </button>
                </div>
                {setPasswordMutation.isError && (
                  <p className="text-[10px] text-red-500">Erreur lors de la définition du mot de passe.</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {recoveryKey && (
        <RecoveryKeyModal
          recoveryKey={recoveryKey}
          onAcknowledge={() => setRecoveryKey(null)}
        />
      )}
    </>
  );
}
